/**
 * Pilcore PMS (Admin)
 * Occupancy Audit screen: compares counts from Reservations vs Forecast Occupancy vs Daily Category Availability.
 * UI only (wiring to navbar/routes comes in next step).
 */

import React, { useCallback, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";
import AdminOccupancyAuditDiffPopup from "./AdminOccupancyAuditDiffPopup";

function AdminOccupancyAudit() {
  const [rows, setRows] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [popupContext, setPopupContext] = useState(null);

  const hasRows = rows && rows.length > 0;

  const columns = useMemo(() => {
    return [
      { key: "business_unit_name", label: "Business Unit", align: "center" },
      { key: "stay_date", label: "Date", align: "center" },
      { key: "room_category_name", label: "Room Category", align: "center" },
      { key: "reservations_count", label: "Reservations", align: "center" },
      { key: "forecast_occupancy_count", label: "Forecast Occupancy", align: "center" },
      { key: "daily_category_availability_occupied_rooms", label: "Daily Category Availability", align: "center" },
      { key: "diff", label: "Diff", align: "center" },
    ];
  }, []);

  const computeDiff = useCallback((r) => {
    const a = Number(r?.reservations_count ?? 0);
    const b = Number(r?.forecast_occupancy_count ?? 0);
    const c = Number(r?.daily_category_availability_occupied_rooms ?? 0);
    // Simple highlight: any mismatch across the three
    const max = Math.max(a, b, c);
    const min = Math.min(a, b, c);
    return max - min;
  }, []);

  const runCheck = useCallback(async () => {
    console.log("[OCC_AUDIT][UI] Run check clicked");
    setIsRunning(true);
    setErrorMsg("");

    try {
      const resp = await axios.get("/api/admin/occupancy-audit", {
        params: { days_forward: 365 },
      });

      const payload = resp?.data || {};
      console.log("[OCC_AUDIT][UI] response.payload", payload);

      const data = Array.isArray(payload.rows) ? payload.rows : [];
      console.log("[OCC_AUDIT][UI] rows.length", data.length);
      console.log("[OCC_AUDIT][UI] filters", payload.filters);

      const enriched = data.map((r) => ({
        ...r,
        diff: computeDiff(r),
      }));

      console.log("[OCC_AUDIT][UI] Loaded rows", { count: enriched.length });
      setRows(enriched);
    } catch (err) {
      console.error("[OCC_AUDIT][UI] Failed to run check", err);
      setRows([]);
      setErrorMsg(err?.response?.data?.error || "Failed to run occupancy audit.");
    } finally {
      setIsRunning(false);
    }
  }, [computeDiff]);

  function handleDiffClick(row) {
    const diff = Number(row?.diff || 0);
    if (!diff) return;

    const nextContext = {
      business_unit_id: Number(row?.business_unit_id),
      business_unit_name: row?.business_unit_name || null,
      room_category_id: Number(row?.room_category_id),
      room_category_name: row?.room_category_name || null,
      stay_date: row?.stay_date ? String(row.stay_date).slice(0, 10) : null,
    };

    if (!nextContext.business_unit_id || !nextContext.room_category_id || !nextContext.stay_date) {
      console.warn("[OCC_AUDIT][UI] diff click ignored due to missing context", row);
      return;
    }

    console.log("[OCC_AUDIT][UI] click diff", nextContext);
    setPopupContext(nextContext);
  }


  return (
    <div>
      <h2 style={{ marginBottom: "12px" }}>Occupancy Audit</h2>

      <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "12px" }}>
        <button
          className="btn btn-primary"
          onClick={runCheck}
          disabled={isRunning}
          type="button"
        >
          {isRunning ? "Running..." : "Run check"}
        </button>

        <div style={{ opacity: 0.85 }}>
          Compares counts for the next 365 days: Reservations vs Forecast Occupancy vs Daily Category Availability.
        </div>
      </div>

      {errorMsg ? (
        <div className="alert alert-danger" style={{ marginBottom: "12px" }}>
          {errorMsg}
        </div>
      ) : null}

      <div
        className="table-responsive"
        style={{
          maxHeight: "500px",
          overflowY: "auto",
          overflowX: "auto",
        }}
      >
        <table
          className="table table-dark table-striped table-sm align-middle mb-0"
          style={{
            fontSize: "0.78rem",
            lineHeight: "1.0rem",
            tableLayout: "auto",
            width: "auto",
            minWidth: "1100px",
            whiteSpace: "nowrap",
          }}
        >
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ textAlign: c.align || "left" }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {!hasRows ? (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center", opacity: 0.8 }}>
                  No data loaded. Click “Run check”.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => {
                const diff = Number(r.diff || 0);
                const highlight = diff !== 0;
                const isClickable = highlight;

                return (
                  <tr key={`${r.business_unit_id}-${r.stay_date}-${r.room_category_id}-${idx}`}>
                                        <td style={{ textAlign: "center" }}>
                      {r.business_unit_name ?? r.business_unit_id ?? "-"}
                    </td>
                    <td style={{ textAlign: "center" }}>{r.stay_date}</td>
                    <td style={{ textAlign: "center" }}>
                      {r.room_category_name ?? r.room_category_id ?? "-"}
                    </td>


                    <td style={{ textAlign: "center" }}>{r.reservations_count ?? 0}</td>
                    <td style={{ textAlign: "center" }}>{r.forecast_occupancy_count ?? 0}</td>
                    <td style={{ textAlign: "center" }}>{r.daily_category_availability_occupied_rooms ?? 0}</td>

                    <td
                      style={{
                        textAlign: "center",
                        fontWeight: highlight ? "bold" : "normal",
                        background: highlight ? "#5a1f1f" : "transparent",
                        cursor: isClickable ? "pointer" : "default",
                        textDecoration: isClickable ? "underline" : "none",
                      }}
                      title={isClickable ? "Click to inspect reservations" : ""}
                      onClick={() => isClickable && handleDiffClick(r)}
                    >
                      {diff}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <AdminOccupancyAuditDiffPopup
        isOpen={Boolean(popupContext)}
        context={popupContext}
        onClose={() => setPopupContext(null)}
        onRebuildSuccess={async (result) => {
          console.log("[OCC_AUDIT][UI] rebuild completed", result);
          await runCheck();
          setPopupContext(null);
        }}
      />
    </div>
  );
}

export default AdminOccupancyAudit;
